// File: src/components/OmniCine/Timeline/CommandPalette.tsx
//
// OmniCine — Command Palette (Neon Glass)
// - No mojibake (UTF-8 safe strings)
// - Strong key capture while open (prevents "A/T/I" page shortcuts firing)
// - Deterministic highlight + preview emission
// - Ripple + border pulse retained
// - Uses OmniCineTimeline canonical dispatch for marker/snap/zoom
//
// Notes:
// - This component does NOT control HUD visibility.
// - HUD/Inspector show/hide is owned by src/pages/OmniCine.tsx.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useOmniCineTimeline } from "@/state/useOmniCineTimeline";

type Cmd = { id: string; label: string; keywords?: string; run: () => void };

type Props = {
  open: boolean;
  onClose: () => void;
  container?: HTMLElement | null;
};

type Range = { start: number; end: number };

const emit = (type: string, detail?: unknown) => {
  window.dispatchEvent(new CustomEvent(type, { detail }));
};

function previewFor(cmdId: string) {
  if (cmdId === "preset.gentle") emit("omnicine:preset.preview", { key: "gentle-fade", scope: "selection" });
  if (cmdId === "preset.urgent") emit("omnicine:preset.preview", { key: "urgent-cut", scope: "selection" });
  if (cmdId === "preset.cine") emit("omnicine:preset.preview", { key: "cinematic-swell", scope: "selection" });
}

function clearPreview() {
  emit("omnicine:preset.clear");
}

/* --------------------------------------------------------------- */
/* small utils                                                      */
/* --------------------------------------------------------------- */

function finiteNum(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

// --- sound cues (best-effort) -------------------------------------
function playPresetCue(kind: "gentle" | "urgent" | "cine" | "generic" = "generic") {
  try {
    const Ctx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined;
    if (!Ctx) return;

    const ctx = new Ctx();
    const t0 = ctx.currentTime + 0.01;

    const o = ctx.createOscillator();
    const g = ctx.createGain();

    if (kind === "gentle") {
      o.type = "sine";
      o.frequency.setValueAtTime(440, t0);
      o.frequency.exponentialRampToValueAtTime(660, t0 + 0.1);
    } else if (kind === "urgent") {
      o.type = "square";
      o.frequency.setValueAtTime(880, t0);
      o.frequency.exponentialRampToValueAtTime(520, t0 + 0.08);
    } else if (kind === "cine") {
      o.type = "sawtooth";
      o.frequency.setValueAtTime(330, t0);
      o.frequency.exponentialRampToValueAtTime(495, t0 + 0.12);
    } else {
      o.type = "triangle";
      o.frequency.setValueAtTime(660, t0);
    }

    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.08, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.14);

    o.connect(g).connect(ctx.destination);
    o.start(t0);
    o.stop(t0 + 0.18);

    // cleanup
    setTimeout(() => {
      try {
        ctx.close();
      } catch {}
    }, 350);
  } catch {}
}

function cueFor(cmdId: string) {
  if (cmdId === "preset.gentle") return "gentle" as const;
  if (cmdId === "preset.urgent") return "urgent" as const;
  if (cmdId === "preset.cine") return "cine" as const;
  return "generic" as const;
}

export default function CommandPalette({ open, onClose, container }: Props) {
  const { state, dispatch } = useOmniCineTimeline();

  const [q, setQ] = useState("");
  const [index, setIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  // selection range from spectrum drag (optional, used by preset system)
  const selectionRef = useRef<Range | null>(null);
  useEffect(() => {
    const onRange = (e: Event) => {
      const det = (e as CustomEvent).detail as Partial<Range> | undefined;
      const start = det?.start;
      const end = det?.end;
      if (typeof start === "number" && typeof end === "number") {
        selectionRef.current = { start, end };
      }
    };
    window.addEventListener("timeline:range", onRange as EventListener);
    return () => window.removeEventListener("timeline:range", onRange as EventListener);
  }, []);

  const addMarkerAtPlayhead = useCallback(() => {
    const t = typeof (state as any).playheadSec === "number" ? Number((state as any).playheadSec) : 0;
    const fps = Number((state as any)?.snap?.fps ?? 30) || 30;

    dispatch({
      type: "marker/add",
      payload: {
        marker: {
          id: crypto.randomUUID(),
          timeSec: t,
          label: "Beat",
          type: "beat",
          note: "Beat marker",
          description: "Beat marker",
          frameIndex: Math.max(0, Math.round(t * fps)),
          visible: true,
          source: "user",
        },
      },
      meta: { source: "user" },
    } as any);
  }, [dispatch, state]);

  const toggleSnap = useCallback(() => {
    const enabled = !!(state as any)?.snap?.enabled;

    dispatch({
      type: "timeline/setSnap",
      payload: { enabled: !enabled },
      meta: { source: "user" },
    } as any);
  }, [dispatch, state]);

  // Canonical zoom read (matches TimelineShell/TransportBar fallback strategy)
  const viewport = (state as any)?.viewport ?? null;
  const pxPerSec =
    finiteNum(viewport?.pxPerSec) ? Number(viewport.pxPerSec) : finiteNum((state as any)?.pxPerSec) ? Number((state as any).pxPerSec) : 100;

  const zoomIn = useCallback(() => {
    const next = clamp(pxPerSec * 1.1, 20, 400);

    dispatch({
      type: "timeline/setZoom",
      payload: { pxPerSec: next },
      meta: { source: "user" },
    } as any);
  }, [dispatch, pxPerSec]);

  const zoomOut = useCallback(() => {
    const next = clamp(pxPerSec * 0.9, 20, 400);

    dispatch({
      type: "timeline/setZoom",
      payload: { pxPerSec: next },
      meta: { source: "user" },
    } as any);
  }, [dispatch, pxPerSec]);

  const runPreset = useCallback((key: string) => {
    window.dispatchEvent(
      new CustomEvent("omnicine:preset", {
        detail: { key, scope: "selection", range: selectionRef.current },
      })
    );
  }, []);

  const baseCommands = useMemo<Cmd[]>(
    () => [
      {
        id: "preset.gentle",
        label: "AI: Gentle Fade (Selection)",
        keywords: "fade smooth dissolve envelope",
        run: () => runPreset("gentle-fade"),
      },
      {
        id: "preset.urgent",
        label: "AI: Urgent Cut (Selection)",
        keywords: "hard cut punch staccato",
        run: () => runPreset("urgent-cut"),
      },
      {
        id: "preset.cine",
        label: "AI: Cinematic Swell (Selection)",
        keywords: "cinematic swell crescendo",
        run: () => runPreset("cinematic-swell"),
      },

      {
        id: "marker.beat",
        label: "Add Marker: Beat @ Playhead",
        keywords: "marker beat flag cue",
        run: () => addMarkerAtPlayhead(),
      },
      {
        id: "snap.toggle",
        label: `Toggle Snap (${(state as any)?.snap?.enabled ? "On" : "Off"})`,
        keywords: "grid snap quantize",
        run: () => toggleSnap(),
      },

      { id: "zoom.in", label: "Zoom In", keywords: "zoom in", run: () => zoomIn() },
      { id: "zoom.out", label: "Zoom Out", keywords: "zoom out", run: () => zoomOut() },
    ],
    [runPreset, addMarkerAtPlayhead, toggleSnap, zoomIn, zoomOut, state]
  );

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return baseCommands;
    return baseCommands.filter((c) => (c.label + " " + (c.keywords ?? "")).toLowerCase().includes(needle));
  }, [q, baseCommands]);

  // Keep index valid if filter reduces list size
  useEffect(() => {
    setIndex((i) => Math.max(0, Math.min(i, Math.max(0, items.length - 1))));
  }, [items.length]);

  // Focus on open
  useEffect(() => {
    if (!open) return;
    setQ("");
    setIndex(0);

    // Focus next tick
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  // Execute command helper (single pathway for Enter/click)
  const execute = useCallback(
    (cmd: Cmd | undefined, opts?: { rippleIndex?: number }) => {
      if (!cmd) return;

      // ripple on active row
      const ri = opts?.rippleIndex;
      if (typeof ri === "number") {
        const li = listRef.current?.querySelectorAll("li")[ri] as HTMLElement | undefined;
        if (li) {
          li.style.setProperty("--rx", `${li.clientWidth / 2}px`);
          li.style.setProperty("--ry", `${li.clientHeight / 2}px`);
          // restart ripple
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          li.offsetHeight;
          li.classList.add("cp-ripple-kick");
          setTimeout(() => li.classList.remove("cp-ripple-kick"), 260);
        }
      }

      playPresetCue(cueFor(cmd.id));
      cmd.run();

      // close + clear preview state
      onClose();
      clearPreview();
    },
    [onClose]
  );

  // Key capture while open (prevents Page shortcuts firing)
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const k = e.key;

      // Always capture navigation keys while open
      if (k === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        clearPreview();
        return;
      }

      if (k === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setIndex((i) => Math.min(i + 1, items.length - 1));
        return;
      }

      if (k === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setIndex((i) => Math.max(i - 1, 0));
        return;
      }

      if (k === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        execute(items[index], { rippleIndex: index });
        return;
      }

      // Important: stop other single-key shortcuts (A/T/I etc.) from leaking
      // while still allowing typing into the input.
      // If focus is in the input, we should not suppress normal character entry.
      const active = document.activeElement;
      const isTyping =
        active === inputRef.current || active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;

      if (!isTyping) {
        // Prevent the page from interpreting single keys as actions.
        // Do not block Ctrl/Cmd combos (copy/paste etc.)
        if (!(e.metaKey || e.ctrlKey || e.altKey)) {
          e.stopPropagation();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown, true); // capture phase
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open, items, index, onClose, execute]);

  // Preview follows highlight
  useEffect(() => {
    if (!open) return;
    const cmd = items[index];
    if (cmd) previewFor(cmd.id);
    return () => clearPreview();
  }, [open, items, index]);

  if (!open) return null;

  const node = (
    <div
      aria-modal
      role="dialog"
      className="fixed inset-0 z-[2000] flex items-start justify-center p-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
          clearPreview();
        }
      }}
    >
      {/* dimmer */}
      <div className="absolute inset-0 bg-black/40" />

      {/* frosted glass shell */}
      <div
        className={[
          "relative w-full max-w-2xl rounded-2xl overflow-hidden",
          "backdrop-blur-2xl border",
          "border-white/15 shadow-[0_20px_70px_-16px_rgba(0,0,0,0.6)]",
          "animate-[palettePop_200ms_ease-out]",
        ].join(" ")}
        style={{
          background:
            "linear-gradient(180deg, rgba(18,28,38,0.70), rgba(12,16,24,0.70))," +
            "radial-gradient(120% 100% at 10% 0%, rgba(34,211,238,0.20), transparent 60%)," +
            "radial-gradient(120% 100% at 90% 100%, rgba(168,85,247,0.18), transparent 60%)",
        }}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/20" />

        {/* input */}
        <div className="px-4 pt-4">
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type a command…"
            className={[
              "w-full bg-white/5 text-white placeholder-white/70",
              "outline-none text-sm px-3 py-2.5",
              "rounded-lg border border-white/15",
              "focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/40",
              "transition shadow-[0_0_0_0_rgba(0,0,0,0)] focus:shadow-[0_0_20px_2px_rgba(34,211,238,0.25)]",
            ].join(" ")}
            style={{
              caretColor: "rgb(34 211 238)",
              textShadow: "0 0 6px rgba(34,211,238,0.15)",
            }}
          />
        </div>

        {/* list */}
        <ul ref={listRef} className="max-h-80 overflow-auto py-2 text-white">
          {items.length === 0 && <li className="px-4 py-3 text-white/70 text-sm">No matches.</li>}

          {items.map((c, i) => {
            // dynamic theme vars for ripple + pulse
            const cyanRGB = "34 211 238";
            const violetRGB = "168 85 247";
            const isActive = i === index;

            return (
              <li
                key={c.id}
                className={[
                  "relative mx-2 my-1 rounded-lg px-3 py-2 text-sm cursor-pointer select-none overflow-hidden",
                  isActive ? "text-white bg-white/14 ring-1 ring-white/25 cp-pulse" : "text-white/90 hover:bg-white/8",
                  "cp-ripple transition-colors",
                ].join(" ")}
                style={{ ["--cp-c0" as any]: cyanRGB, ["--cp-c1" as any]: violetRGB }}
                onMouseEnter={() => setIndex(i)}
                onMouseDown={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  const r = el.getBoundingClientRect();
                  el.style.setProperty("--rx", `${e.clientX - r.left}px`);
                  el.style.setProperty("--ry", `${e.clientY - r.top}px`);
                  e.preventDefault();
                }}
                onClick={() => execute(c, { rippleIndex: i })}
              >
                <span className="relative z-10 drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)]">{c.label}</span>
              </li>
            );
          })}
        </ul>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/20 to-transparent" />
      </div>

      {/* keyframes + component CSS */}
      <style>{`
        @keyframes palettePop {
          from {
            transform: translateY(6px) scale(0.98);
            opacity: 0;
            box-shadow: 0 10px 30px -14px rgba(0,0,0,0.45);
          }
          to {
            transform: translateY(0) scale(1);
            opacity: 1;
            box-shadow: 0 20px 70px -16px rgba(0,0,0,0.60);
          }
        }

        /* border pulse on active item (uses dynamic hues) */
        @keyframes borderPulse {
          0%   { --cp-alpha: .35; }
          50%  { --cp-alpha: .85; }
          100% { --cp-alpha: .35; }
        }
        .cp-pulse::after {
          content: "";
          position: absolute;
          inset: -1px;
          border-radius: 0.5rem; /* rounded-lg */
          padding: 1px;
          background:
            conic-gradient(from 0deg,
              rgba(var(--cp-c0, 34 211 238), var(--cp-alpha, .5)) 0deg,
              rgba(var(--cp-c1, 168 85 247), var(--cp-alpha, .5)) 120deg,
              rgba(var(--cp-c0, 34 211 238), var(--cp-alpha, .5)) 360deg);
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor; mask-composite: exclude;
          animation: borderPulse 1.8s cubic-bezier(.45,.05,.35,.95) infinite;
          pointer-events: none;
        }

        /* Ripple adopts the same hues via vars */
        @keyframes cpRipple {
          0%   { transform: scale(0);    opacity: .35; }
          60%  { transform: scale(1);    opacity: .18; }
          100% { transform: scale(1.15); opacity: 0; }
        }
        .cp-ripple::before {
          content: "";
          position: absolute;
          left: var(--rx, 50%);
          top: var(--ry, 50%);
          width: 240px; height: 240px;
          margin-left: -120px; margin-top: -120px;
          border-radius: 9999px;
          background: radial-gradient(closest-side,
            rgba(var(--cp-c0, 34 211 238), 0.35),
            rgba(var(--cp-c1, 168 85 247), 0.28) 40%,
            rgba(var(--cp-c1, 168 85 247), 0.00) 70%);
          filter: blur(0.3px);
          pointer-events: none;
          transform: scale(0);
          opacity: 0;
        }
        .cp-ripple:active::before,
        .cp-ripple-kick.cp-ripple::before {
          animation: cpRipple 260ms cubic-bezier(.24,.6,.2,1) forwards;
        }

        @media (prefers-reduced-motion: reduce) {
          .cp-ripple:active::before,
          .cp-ripple-kick.cp-ripple::before,
          .cp-pulse::after {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );

  return createPortal(node, container ?? document.body);
}
